/**
 * TT-1378 / CR-10713: detect a browser that will not render images at all.
 *
 * When Chrome's "Images" content setting is Block for the site (site setting,
 * ImagesBlockedForUrls policy, or an extension using chrome.contentSettings),
 * an <img> fires neither `load` nor `error` — it just never settles. Every
 * image in the app then silently fails: the Spine avatar textures (init()
 * hangs), the reader panel frame, the mic icon, and the static Amira fallback.
 * Nothing in the app can observe that without a probe.
 *
 * This loads a 1x1 inline PNG (no network, no CDN, no cache) and races it
 * against a timeout. A healthy browser settles in single-digit milliseconds.
 *
 * Scope: a `loaded` result proves the browser will load and decode images. It
 * says nothing about WebGL texture upload or rendering, which is a separate
 * failure class (see the webgl_* telemetry in WebGLContext).
 */

export const IMAGE_PROBE_TIMEOUT_MS = 2000

// If the timeout timer fires this much later than it was due, the main thread was
// stalled. A genuine image block overshoots by a couple of milliseconds (measured
// 2002 ms for a 2000 ms timer); a frozen event loop overshoots by the stall length.
// A stalled timeout is inconclusive, not evidence of a block.
export const IMAGE_PROBE_STALL_SLACK_MS = 500

export const IMAGE_PROBE_OUTCOMES = {
    loaded: 'loaded',
    errored: 'errored',
    timeout: 'timeout',
    stalled: 'stalled',
    unsupported: 'unsupported',
}

// 1x1 transparent PNG
export const IMAGE_PROBE_DATA_URI =
    'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII='

/**
 * @param {object} [options]
 * @param {number} [options.timeoutMs] - how long to wait before declaring images blocked.
 * @param {Function} [options.ImageCtor] - injectable Image constructor (tests).
 * @param {Function} [options.now] - injectable monotonic clock in ms (tests).
 * @returns {Promise<{blocked: boolean, outcome: string, elapsedMs: number, overshootMs?: number}>}
 *   `blocked` is true only for `timeout` — the documented signature of a browser
 *   image block (neither event fires, and the timer fired on time). `stalled` is
 *   a timeout whose timer fired far past due: the main thread was frozen, so the
 *   result is inconclusive and does not block. `errored` means the inline PNG
 *   failed to decode, which is a different, unexpected condition: reported for
 *   telemetry, never blocks. `unsupported` means there is no Image constructor
 *   (non-browser environment) — no signal either way.
 */
export default function probeImageRendering({ timeoutMs = IMAGE_PROBE_TIMEOUT_MS, ImageCtor, now: nowOverride } = {}) {
    const Ctor = ImageCtor || (typeof Image !== 'undefined' ? Image : null)

    return new Promise((resolve) => {
        if (!Ctor) {
            resolve({ blocked: false, outcome: IMAGE_PROBE_OUTCOMES.unsupported, elapsedMs: 0 })
            return
        }

        const now = nowOverride || (() => (typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now()))
        const startedAt = now()
        let settled = false
        let timer = null
        const img = new Ctor()

        const finish = (rawOutcome) => {
            if (settled) return
            settled = true
            if (timer) clearTimeout(timer)
            img.onload = null
            img.onerror = null
            if (rawOutcome === IMAGE_PROBE_OUTCOMES.timeout) img.src = '' // drop the in-flight load
            const elapsedMs = Math.round(now() - startedAt)
            let outcome = rawOutcome
            const result = { outcome, elapsedMs }
            if (rawOutcome === IMAGE_PROBE_OUTCOMES.timeout) {
                const overshootMs = elapsedMs - timeoutMs
                result.overshootMs = overshootMs
                if (overshootMs > IMAGE_PROBE_STALL_SLACK_MS) outcome = IMAGE_PROBE_OUTCOMES.stalled
            }
            result.outcome = outcome
            result.blocked = outcome === IMAGE_PROBE_OUTCOMES.timeout
            resolve(result)
        }

        timer = setTimeout(() => finish(IMAGE_PROBE_OUTCOMES.timeout), timeoutMs)
        img.onload = () => finish(IMAGE_PROBE_OUTCOMES.loaded)
        img.onerror = () => finish(IMAGE_PROBE_OUTCOMES.errored)

        img.src = IMAGE_PROBE_DATA_URI
    })
}
